/**
 * Threshold function preview rendered with function-plot and KaTeX.
 */

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { UIThresholdModel } from './catalog-transform';

// -- type definitions --

export interface ThresholdFunctionPreviewProps {
  readonly threshold: UIThresholdModel;
  readonly height?: number;
}

type PlotSeries = {
  readonly fn?: string;
  readonly fnType?: 'linear' | 'parametric' | 'points';
  readonly x?: string;
  readonly y?: string;
  readonly points?: Array<[number, number]>;
  readonly range?: [number, number];
  readonly color?: string;
  readonly graphType?: 'polyline';
  readonly closed?: boolean;
  readonly nSamples?: number;
  readonly attr?: Record<string, string | number>;
};

export interface PlotSpec {
  readonly formula: string;
  readonly xDomain: [number, number];
  readonly yDomain: [number, number];
  readonly data: readonly PlotSeries[];
}

interface PiecewiseSegment {
  readonly lower: number;
  readonly upper: number;
  readonly expression: string;
  readonly numericValue: number | null;
}

// -- constants --

const DEFAULT_HEIGHT = 112;
const DEFAULT_X_DOMAIN: [number, number] = [-6, 6];
const DEFAULT_Y_DOMAIN: [number, number] = [-6, 6];
const MIN_DOMAIN_SPAN = 4;
const MAX_DOMAIN_ABS = 250;

// -- styled components --

const drawAndErase = keyframes`
0% {
    stroke-dashoffset: 1000;
  }
  5%, 95% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: -1000;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    text-shadow: 
      0 0 5px rgba(255, 255, 255, 0.15),
      0 0 15px rgba(0, 255, 170, 0.1);
  }
  50% {
    text-shadow: 
      0 0 8px rgba(255, 255, 255, 0.3),
      0 0 25px rgba(0, 255, 170, 0.2);
  }
`;

const PreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 112px;
  border-radius: 0;
  overflow: hidden;
  background: #1e293b;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.28);
  isolation: isolate;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  .axis,
  .origin,
  .domain,
  .tick,
  .tip,
  text {
    display: none !important;
  }

  .function-plot,
  svg {
    display: block;
    width: 100% !important;
    max-width: 100%;
    height: 100% !important;
    overflow: hidden;
  }

  path.line {
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.3)) !important;

    /* swipe effect */
    stroke-dasharray: 1000;
    animation: ${drawAndErase} 10s ease-in-out infinite;
  }
`;

const PlotSurface = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  min-width: 0;
  height: 112px;
  max-width: 100%;
  padding: 10px;
  overflow: hidden;
  box-sizing: border-box;
  contain: layout paint;
`;

const FormulaOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.3);
  pointer-events: none;
  z-index: 1;
  width: calc(100% - 24px);
  max-width: calc(100% - 24px);
  overflow: hidden;
  white-space: nowrap;
  text-align: center;

  .katex-display {
    margin: 0;
    overflow: hidden;
  }

  .katex {
    display: block;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: clamp(3rem, 1.4vw, 3.2rem);
    font-weight: 800;

    /* neon glow effect */
    /* the first layer is a tight glow and the last is the most diffuse */
    line-height: 1.5;
    padding: 20px 0;
    animation: ${pulseGlow} 3s ease-in-out infinite;
  }
`;

const FallbackLabel = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  z-index: 0;
`;

// -- functions --

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '0';
  }

  const rounded = Math.abs(value) < 1e-9 ? 0 : value;
  return Number.isInteger(rounded)
    ? `${rounded}`
    : rounded.toFixed(2).replace(/\.0+$|(?<=\.[0-9]*)0+$/g, '');
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function normalizeDomain(
  min: number,
  max: number,
  fallback: [number, number]
): [number, number] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return fallback;
  }

  let lower = Math.min(min, max);
  let upper = Math.max(min, max);

  if (lower === upper) {
    const padding = Math.max(Math.abs(lower) * 0.2, MIN_DOMAIN_SPAN / 2, 1);
    lower -= padding;
    upper += padding;
  } else {
    const padding = Math.max((upper - lower) * 0.15, 1);
    lower -= padding;
    upper += padding;
  }

  lower = clamp(lower, -MAX_DOMAIN_ABS, MAX_DOMAIN_ABS);
  upper = clamp(upper, -MAX_DOMAIN_ABS, MAX_DOMAIN_ABS);

  if (upper - lower < MIN_DOMAIN_SPAN) {
    const middle = (upper + lower) / 2;
    return [middle - MIN_DOMAIN_SPAN / 2, middle + MIN_DOMAIN_SPAN / 2];
  }

  return [lower, upper];
}

function matchDomainsToAspectRatio(
  xDomain: [number, number],
  yDomain: [number, number],
  aspectRatio: number
): { xDomain: [number, number]; yDomain: [number, number] } {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return { xDomain, yDomain };
  }

  const xSpan = xDomain[1] - xDomain[0];
  const ySpan = yDomain[1] - yDomain[0];

  if (xSpan <= 0 || ySpan <= 0) {
    return { xDomain, yDomain };
  }

  const currentRatio = xSpan / ySpan;

  if (Math.abs(currentRatio - aspectRatio) < 0.02) {
    return { xDomain, yDomain };
  }

  if (currentRatio < aspectRatio) {
    const desiredXSpan = ySpan * aspectRatio;
    const xCenter = (xDomain[0] + xDomain[1]) / 2;
    return {
      xDomain: [xCenter - desiredXSpan / 2, xCenter + desiredXSpan / 2],
      yDomain,
    };
  }

  const desiredYSpan = xSpan / aspectRatio;
  const yCenter = (yDomain[0] + yDomain[1]) / 2;

  return {
    xDomain,
    yDomain: [yCenter - desiredYSpan / 2, yCenter + desiredYSpan / 2],
  };
}

function getAccentColor(threshold: UIThresholdModel): string {
  return threshold.defaultStyle?.color ?? '#5bcefa';
}

function toLatexExpression(expression: string): string {
  return expression
    .replace(/\*/g, ' \\cdot ')
    .replace(/<=/g, ' \\le ')
    .replace(/>=/g, ' \\ge ');
}

function formatSignedLatexTerm(coefficient: number, power: number): string {
  const absolute = Math.abs(coefficient);
  const sign = coefficient < 0 ? '-' : '+';

  if (power === 0) {
    return `${sign} ${formatNumber(absolute)}`;
  }

  const coefficientPart = absolute === 1 ? '' : formatNumber(absolute);
  const variablePart = power === 1 ? 'x' : `x^{${power}}`;

  return `${sign} ${coefficientPart}${variablePart}`.trim();
}

function readPolynomialCoefficients(params: Record<string, unknown>): number[] {
  const rawCoefficients = Array.isArray(params.coefficients)
    ? params.coefficients
    : Array.isArray(params.coeffs)
    ? params.coeffs
    : [];

  const safeCoefficients = rawCoefficients
    .map((value) => toNumber(value, Number.NaN))
    .filter((value) => Number.isFinite(value));

  return safeCoefficients.length > 0 ? safeCoefficients : [1, 0, 0];
}

function buildPolynomialExpression(coefficients: readonly number[]): string {
  if (coefficients.length === 0) {
    return '0';
  }

  return coefficients
    .map((coefficient, index) => {
      const power = coefficients.length - index - 1;
      if (power === 0) {
        return `${coefficient}`;
      }
      if (power === 1) {
        return `${coefficient} * x`;
      }
      return `${coefficient} * x^${power}`;
    })
    .join(' + ');
}

function buildPolynomialLatex(coefficients: readonly number[]): string {
  const terms = coefficients
    .map((coefficient, index) => {
      const power = coefficients.length - index - 1;
      if (coefficient === 0) {
        return null;
      }
      return formatSignedLatexTerm(coefficient, power);
    })
    .filter((term): term is string => term !== null);

  if (terms.length === 0) {
    return '0';
  }

  return terms.join(' ').replace(/^\+\s*/, '');
}

function evaluatePolynomial(
  coefficients: readonly number[],
  x: number
): number {
  return coefficients.reduce(
    (accumulator, coefficient) => accumulator * x + coefficient,
    0
  );
}

function buildPiecewiseSegments(
  params: Record<string, unknown>
): PiecewiseSegment[] {
  if (!Array.isArray(params.segments)) {
    return [];
  }

  return params.segments.flatMap((segment) => {
    if (typeof segment !== 'object' || segment === null) {
      return [];
    }

    const lower = toNumber((segment as { lower?: unknown }).lower, 0);
    const upper = toNumber((segment as { upper?: unknown }).upper, lower + 1);
    const rawExpression = (segment as { expression?: unknown; value?: unknown })
      .expression;
    const rawValue = (segment as { value?: unknown }).value;
    const expression =
      typeof rawExpression === 'string'
        ? rawExpression
        : formatNumber(toNumber(rawValue, 0));

    const numericValue =
      typeof rawExpression === 'string' && rawExpression.trim() !== ''
        ? Number(rawExpression)
        : toNumber(rawValue, Number.NaN);

    return [
      {
        lower,
        upper,
        expression,
        numericValue: Number.isFinite(numericValue) ? numericValue : null,
      },
    ];
  });
}

function normalizePolarAngle(value: number): number {
  const numericValue =
    Math.abs(value) > Math.PI * 2 ? (value * Math.PI) / 180 : value;
  return clamp(numericValue, -Math.PI * 2, Math.PI * 2);
}

function buildPolarDomain(radiusValues: readonly number[]): [number, number] {
  const maxRadius =
    radiusValues.length > 0
      ? Math.max(...radiusValues.map((value) => Math.abs(value)))
      : 4;
  const span = Math.max(maxRadius * 1.25, 4);
  return [-span, span];
}

function buildPolarAngleRange(lower: number, upper: number): [number, number] {
  const start = normalizePolarAngle(lower);
  const end = normalizePolarAngle(upper);

  if (end > start) {
    return [start, end];
  }

  return [start, Math.min(start + Math.PI / 18, Math.PI * 2)];
}

function toParametricExpression(expression: string): string {
  return expression.replace(/\btheta\b/g, 't').replace(/\bx\b/g, 't');
}

function buildParametricPolarSeries(
  radiusExpression: string,
  range: [number, number],
  color: string,
  options?: {
    readonly closed?: boolean;
    readonly nSamples?: number;
    readonly attr?: Record<string, string | number>;
  }
): PlotSeries {
  const normalizedExpression = toParametricExpression(radiusExpression);

  return {
    fnType: 'parametric',
    x: `(${normalizedExpression}) * cos(t)`,
    y: `(${normalizedExpression}) * sin(t)`,
    range,
    color,
    graphType: 'polyline',
    closed: options?.closed,
    nSamples: options?.nSamples,
    attr: options?.attr,
  };
}

function buildPiecewiseLatex(
  segments: readonly PiecewiseSegment[],
  variableName = 'x',
  functionName = 'f'
): string {
  if (segments.length === 0) {
    return `${functionName}(${variableName}) = 0`;
  }

  const body = segments
    .map(
      (segment) =>
        `${toLatexExpression(segment.expression)} & ${formatNumber(
          segment.lower
        )} \\le ${variableName} < ${formatNumber(segment.upper)}`
    )
    .join(' \\\\ ');

  return `${functionName}(${variableName}) = \\begin{cases} ${body} \\end{cases}`;
}

/**
 * Creates additional series for filling the area under/above the curve.
 * Used to visualize the threshold's fill color (fill_color).
 */
function buildFillAreaSeries(
  originalSeries: PlotSeries,
  xDomain: [number, number],
  yDomain: [number, number],
  boundKind: 'minimum' | 'maximum' | undefined,
  fillColor: string | null | undefined,
  evaluateY?: (x: number) => number
): PlotSeries[] {
  if (!boundKind || !fillColor || !evaluateY) {
    return [];
  }

  const sampleCount = 120;
  const xStart = xDomain[0];
  const xEnd = xDomain[1];
  const xStep = (xEnd - xStart) / sampleCount;
  const curvePoints: Array<[number, number]> = [];

  for (let index = 0; index <= sampleCount; index += 1) {
    const x = xStart + xStep * index;
    const y = Number(evaluateY(x));

    if (!Number.isFinite(y)) {
      return [];
    }

    curvePoints.push([x, clamp(y, yDomain[0], yDomain[1])]);
  }

  if (curvePoints.length < 2) {
    return [];
  }

  const referenceY = boundKind === 'minimum' ? yDomain[0] : yDomain[1];

  const fillPolygonPoints: Array<[number, number]> =
    boundKind === 'minimum'
      ? [[xStart, referenceY], ...curvePoints, [xEnd, referenceY]]
      : [[xStart, referenceY], [xEnd, referenceY], ...curvePoints.reverse()];

  return [
    {
      fnType: 'points',
      points: fillPolygonPoints,
      color: fillColor,
      graphType: 'polyline',
      attr: {
        fill: fillColor,
        'fill-opacity': '0.2',
        stroke: 'none',
        'stroke-width': '0',
      },
      closed: true,
    },
  ];
}

export function buildThresholdPlotSpec(threshold: UIThresholdModel): PlotSpec {
  const params = threshold.rawParameters;
  const color = getAccentColor(threshold);
  const isPolarRepresentation =
    threshold.representationCoordinateSystem === 'polar_2d';

  switch (threshold.type) {
    case 'constant': {
      const value = toNumber(params.c ?? params.value, 0);

      if (isPolarRepresentation) {
        const domain = buildPolarDomain([value]);
        return {
          formula: `r(\\theta) = ${formatNumber(value)}`,
          xDomain: domain,
          yDomain: domain,
          data: [
            buildParametricPolarSeries(`${value}`, [0, Math.PI * 2], color, {
              nSamples: 280,
            }),
          ],
        };
      }

      const xDomain = DEFAULT_X_DOMAIN;
      const yDomain = normalizeDomain(value, value, DEFAULT_Y_DOMAIN);

      const linearSeries: PlotSeries = {
        fn: `${value}`,
        fnType: 'linear',
        color,
        graphType: 'polyline',
        range: xDomain,
      };

      const data: PlotSeries[] = [linearSeries];
      const fillSeries = buildFillAreaSeries(
        linearSeries,
        xDomain,
        yDomain,
        threshold.boundKind,
        threshold.defaultStyle?.fillColor,
        () => value
      );

      return {
        formula: `f(x) = ${formatNumber(value)}`,
        xDomain,
        yDomain,
        data: [...fillSeries, ...data],
      };
    }

    case 'affine': {
      const a = toNumber(params.a, 1);
      const b = toNumber(params.b, 0);
      const intercept =
        b < 0 ? ` - ${formatNumber(Math.abs(b))}` : ` + ${formatNumber(b)}`;

      if (isPolarRepresentation) {
        const thetaRange: [number, number] = [0, Math.PI * 2];
        const radiusSamples = Array.from({ length: 80 }, (_, index) => {
          const theta =
            thetaRange[0] + ((thetaRange[1] - thetaRange[0]) * index) / 79;
          return a * theta + b;
        });
        const domain = buildPolarDomain(radiusSamples);

        return {
          formula: `r(\\theta) = ${formatNumber(a)}\\theta${intercept}`,
          xDomain: domain,
          yDomain: domain,
          data: [
            buildParametricPolarSeries(`${a} * t + ${b}`, thetaRange, color, {
              nSamples: 260,
            }),
          ],
        };
      }

      const xDomain: [number, number] = [-6, 6];
      const yValues = [a * xDomain[0] + b, a * xDomain[1] + b];
      const yDomain = normalizeDomain(
        Math.min(...yValues),
        Math.max(...yValues),
        DEFAULT_Y_DOMAIN
      );

      const affineSeries: PlotSeries = {
        fn: `${a} * x + ${b}`,
        fnType: 'linear',
        color,
        graphType: 'polyline',
        range: xDomain,
      };

      const affineData: PlotSeries[] = [affineSeries];
      const affineFillSeries = buildFillAreaSeries(
        affineSeries,
        xDomain,
        yDomain,
        threshold.boundKind,
        threshold.defaultStyle?.fillColor,
        (x) => a * x + b
      );

      return {
        formula: `f(x) = ${formatNumber(a)}x${intercept}`,
        xDomain,
        yDomain,
        data: [...affineFillSeries, ...affineData],
      };
    }

    case 'polynomial': {
      const coefficients = readPolynomialCoefficients(params);

      if (isPolarRepresentation) {
        const thetaRange: [number, number] = [0, Math.PI * 2];
        const radiusSamples = Array.from({ length: 81 }, (_, index) => {
          const theta =
            thetaRange[0] + ((thetaRange[1] - thetaRange[0]) * index) / 80;
          return evaluatePolynomial(coefficients, theta);
        }).filter((value) => Number.isFinite(value));
        const domain = buildPolarDomain(radiusSamples);
        const polynomialExpression = buildPolynomialExpression(coefficients);

        return {
          formula: `r(\\theta) = ${buildPolynomialLatex(coefficients).replace(
            /x/g,
            '\\theta'
          )}`,
          xDomain: domain,
          yDomain: domain,
          data: [
            buildParametricPolarSeries(
              polynomialExpression,
              thetaRange,
              color,
              { nSamples: 320 }
            ),
          ],
        };
      }

      const xDomain: [number, number] =
        coefficients.length > 4 ? [-3, 3] : [-4, 4];
      const ySamples = Array.from({ length: 81 }, (_, index) => {
        const x = xDomain[0] + ((xDomain[1] - xDomain[0]) * index) / 80;
        return evaluatePolynomial(coefficients, x);
      }).filter((value) => Number.isFinite(value));
      const yDomain =
        ySamples.length > 0
          ? normalizeDomain(
              Math.min(...ySamples),
              Math.max(...ySamples),
              DEFAULT_Y_DOMAIN
            )
          : DEFAULT_Y_DOMAIN;

      return {
        formula: `f(x) = ${buildPolynomialLatex(coefficients)}`,
        xDomain,
        yDomain,
        data: [
          {
            fn: buildPolynomialExpression(coefficients),
            fnType: 'linear',
            color,
            graphType: 'polyline',
            range: xDomain,
            nSamples: 300,
          },
        ],
      };
    }

    case 'piecewise': {
      const segments = buildPiecewiseSegments(params);

      if (isPolarRepresentation) {
        const radiusValues = segments.map(
          (segment) => segment.numericValue ?? toNumber(segment.expression, 3)
        );
        const domain = buildPolarDomain(radiusValues);

        return {
          formula: buildPiecewiseLatex(segments, '\\theta', 'r'),
          xDomain: domain,
          yDomain: domain,
          data:
            segments.length > 0
              ? segments.map((segment) =>
                  buildParametricPolarSeries(
                    segment.numericValue !== null
                      ? `${segment.numericValue}`
                      : segment.expression,
                    buildPolarAngleRange(segment.lower, segment.upper),
                    color,
                    { nSamples: 120 }
                  )
                )
              : [
                  buildParametricPolarSeries('3', [0, Math.PI * 2], color, {
                    nSamples: 220,
                  }),
                ],
        };
      }

      const xBounds = segments.flatMap((segment) => [
        segment.lower,
        segment.upper,
      ]);
      const yValues = segments
        .map((segment) => segment.numericValue)
        .filter((value): value is number => value !== null);

      const xDomain =
        xBounds.length > 0
          ? normalizeDomain(
              Math.min(...xBounds),
              Math.max(...xBounds),
              DEFAULT_X_DOMAIN
            )
          : DEFAULT_X_DOMAIN;
      const yDomain =
        yValues.length > 0
          ? normalizeDomain(
              Math.min(...yValues),
              Math.max(...yValues),
              DEFAULT_Y_DOMAIN
            )
          : DEFAULT_Y_DOMAIN;

      return {
        formula: buildPiecewiseLatex(segments),
        xDomain,
        yDomain,
        data:
          segments.length > 0
            ? segments.map((segment) => ({
                fn: segment.expression,
                fnType: 'linear' as const,
                color,
                graphType: 'polyline' as const,
                range: [segment.lower, segment.upper] as [number, number],
              }))
            : [
                {
                  fn: '0',
                  fnType: 'linear',
                  color,
                  graphType: 'polyline',
                  range: DEFAULT_X_DOMAIN,
                },
              ],
      };
    }

    case 'circle': {
      const radius = Math.max(toNumber(params.radius, 3), 0.5);
      const centerX = toNumber(params.center_x ?? params.cx, 0);
      const centerY = toNumber(params.center_y ?? params.cy, 0);
      const padding = Math.max(radius * 0.35, 1.2);

      return {
        formula: `(x - ${formatNumber(centerX)})^2 + (y - ${formatNumber(
          centerY
        )})^2 = ${formatNumber(radius)}^2`,
        xDomain: normalizeDomain(
          centerX - radius - padding,
          centerX + radius + padding,
          DEFAULT_X_DOMAIN
        ),
        yDomain: normalizeDomain(
          centerY - radius - padding,
          centerY + radius + padding,
          DEFAULT_Y_DOMAIN
        ),
        data: [
          {
            fnType: 'parametric',
            x: `${centerX} + ${radius} * cos(t)`,
            y: `${centerY} + ${radius} * sin(t)`,
            range: [0, Math.PI * 2],
            color,
            graphType: 'polyline',
            closed: true,
            nSamples: 360,
            attr: {
              'stroke-width': 6,
              'stroke-dasharray': '4, 10',
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
            },
          },
        ],
      };
    }

    default:
      return {
        formula: 'f(x) = \\sin(x)',
        xDomain: [-8, 8],
        yDomain: [-2, 2],
        data: [
          {
            fn: 'sin(x)',
            fnType: 'linear',
            color,
            graphType: 'polyline',
            range: [-8, 8],
          },
        ],
      };
  }
}

/**
 * Renders a compact mathematical preview for one threshold card.
 */
export function ThresholdFunctionPreview({
  threshold,
  height = DEFAULT_HEIGHT,
}: ThresholdFunctionPreviewProps) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const plotSpec = useMemo(
    () => buildThresholdPlotSpec(threshold),
    [threshold]
  );
  const [hasRenderedPlot, setHasRenderedPlot] = useState(false);

  const formulaMarkup = useMemo(
    () =>
      katex.renderToString(plotSpec.formula, {
        throwOnError: false,
        displayMode: true,
        strict: 'ignore',
      }),
    [plotSpec.formula]
  );

  useEffect(() => {
    const plotElement = plotRef.current;

    if (!plotElement || typeof window === 'undefined') {
      return;
    }

    if (process.env.NODE_ENV === 'test') {
      return;
    }

    let isDisposed = false;
    let resizeObserver: ResizeObserver | undefined;

    const renderPlot = async () => {
      try {
        const functionPlotModule = await import('function-plot');
        const functionPlot = functionPlotModule.default ?? functionPlotModule;

        if (isDisposed) {
          return;
        }

        const width = Math.max((plotElement.clientWidth || 320) - 8, 180);

        plotElement.innerHTML = '';

        const adjustedDomains =
          threshold.representationCoordinateSystem === 'polar_2d'
            ? matchDomainsToAspectRatio(
                plotSpec.xDomain,
                plotSpec.yDomain,
                width / height
              )
            : { xDomain: plotSpec.xDomain, yDomain: plotSpec.yDomain };

        functionPlot({
          target: plotElement,
          width,
          height,
          disableZoom: true,
          grid: false,
          xAxis: { domain: adjustedDomains.xDomain },
          yAxis: { domain: adjustedDomains.yDomain },
          data: [...plotSpec.data],
        });

        setHasRenderedPlot(true);
      } catch {
        if (!isDisposed) {
          plotElement.innerHTML = '';
        }
        setHasRenderedPlot(false);
      }
    };

    void renderPlot();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        void renderPlot();
      });
      resizeObserver.observe(plotElement);
    }

    return () => {
      isDisposed = true;
      resizeObserver?.disconnect();
      plotElement.innerHTML = '';
    };
  }, [height, plotSpec, threshold.representationCoordinateSystem]);

  return (
    <PreviewWrapper aria-label={`Aperçu de ${threshold.label}`}>
      <FormulaOverlay dangerouslySetInnerHTML={{ __html: formulaMarkup }} />
      <PlotSurface ref={plotRef} style={{ height }} />
      {!hasRenderedPlot ? (
        <FallbackLabel>
          <Typography variant="caption" color="rgba(255,255,255,0.72)">
            Aperçu mathématique
          </Typography>
        </FallbackLabel>
      ) : null}
    </PreviewWrapper>
  );
}

export default ThresholdFunctionPreview;
